import React, {useEffect, useState} from 'react';
import { useFormik } from 'formik';
import { alpha, Button, InputBase, Box, Link, Typography, Stack} from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';

import api from '../../api/api';
import { ArrowForwardIcon } from '../icons';
import { fonts } from '../../theme';

// Each digit gets a field like the form's inputs, its digit set in the brand's mono face
const digitSx = {
  flex: 1,
  minWidth: 0,
  height: 56,
  border: 1,
  borderColor: 'neutral.200',
  borderRadius: '10px',
  backgroundColor: 'common.white',
  fontFamily: fonts.mono,
  fontSize: 22,
  transition: 'border-color 0.2s, box-shadow 0.2s',
  '&:hover': {
    borderColor: 'neutral.400',
  },
  '&.Mui-focused': {
    borderColor: 'primary.main',
    boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
  '& input': {
    textAlign: 'center',
    p: 0,
  },
};

export const StepTwo = ({nextStepCallback, email}) => {
  const [digits, setDigits] = useState(new Array(6).fill(''));
  const inputRefs = Array(6).fill().map(() => React.createRef());
  const [showBackLink, setShowBackLink] = useState(false);
  const submitButtonRef = React.createRef();

  const formik = useFormik({
    initialValues: {
      email: email,
      verificationCode: '',
      submit: ''
    },
    onSubmit: async (values, helpers) => {
      if (values.verificationCode.length < 6) {
        helpers.setErrors({ submit: 'Please enter the 6 digits' });
        return;
      }
      try {
        const response = await api.post('/verify-code', {
          email: values.email,
          verification_code: values.verificationCode
        });
        if(response.status === 200) {
          nextStepCallback(3, values.email.toString(), response.data.signupCode, "", "", false, null, response.data.requirePI);
        }
        else {
          helpers.setErrors({ submit: 'An unexpected error occurred. Please try again!' });
        }
      } catch (error) {
        if (error.response && error.response.data && error.response.data.message) {
          if (error.response.status === 409) {
            if (error.response.data.message === 'admin_already_registered') {
              nextStepCallback(4, values.email.toString(), "", "admin_already_registered");
            } else {
              nextStepCallback(4, values.email.toString(), "", "org_already_registered");
            }
          } else {
            helpers.setErrors({ submit: error.response.data.message });
          }          
        } else {
          helpers.setErrors({ submit: 'An unexpected error occurred. Please try again!' });
        }
        return;
      } 
    }
  })

  useEffect(() => {
    // Focus the first digit input field on component mount
    if (formik.values.verificationCode.length > 0) {
      return;
    }
    if (inputRefs[0].current) {
      inputRefs[0].current.focus();
    }
  }, [inputRefs, formik.values.verificationCode]);

  // Handler for digit input change
  const handleDigitChange = (index, value) => {
    const digit = value.slice(0, 1);
    if (/^\d$/.test(digit)) {
      const newDigits = [...digits];
      newDigits[index] = value.slice(0, 1); // Ensure only one digit is taken
      setDigits(newDigits);
      formik.setFieldValue('verificationCode', newDigits.join(''));    

      // Move focus to the next field if the current one is filled
      if (value && index < digits.length - 1) {
        inputRefs[index + 1].current.focus();
      }
    }
  };

  const resendCode = async () => {
    try {
      const response = await api.post('/resend-code', {
        email: email
      });
      if(response.status === 201) {
        formik.resetForm();
      }
      else {
        formik.setErrors({ submit: 'An unexpected error occurred. Please try again!' });
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        if (error.response.status === 429) {
          formik.setErrors({ submit: 'You have reached the maximum number of attempts. Are you sure you have the correct email address?' });
          setShowBackLink(true);
        }else {
          formik.setErrors({ submit: error.response.data.message });
        }
      }else {
        formik.setErrors({ submit: 'An unexpected error occurred. Please try again!' });
      }
      return;
    }
  }

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text');      
    const pasteDigits = paste.split('').filter((char) => !isNaN(parseInt(char, 10))).slice(0, 6);

    if (pasteDigits.length > 0) {
      const newDigits = [...digits];
      for (let i = 0; i < pasteDigits.length; i++) {
        if (i < newDigits.length) {
          newDigits[i] = pasteDigits[i];
          inputRefs[i].current.value = pasteDigits[i];
        }
      }
      setDigits(newDigits);
      formik.setFieldValue('verificationCode', newDigits.join(''));
      submitButtonRef.current.focus();
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 400,
        mx: 'auto',
      }}
    >
      <Stack
        sx={{
          mb: 2
        }}
      >
        <Typography
          component="h2"
          variant="h3"
          sx={{
            mb: 1.5
          }}
        >
          Check your email for a code
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
        >
          We have sent you a verification email to
          {" "}
          <Link href={`mailto:${email}`}>
            {email}
          </Link>. Please check your inbox and enter the 6 digits provided in the email.
        </Typography>
      </Stack>
      <Stack
        spacing={2}
        sx={{
          mt: 2
        }}
      >
        <Box
          component="form"
          noValidate
          onSubmit={formik.handleSubmit}
        >
          <Stack
            spacing={1.5}
            direction="row"
          >
            {digits.map((_, index) => (
              <InputBase
                key={'input-'+index}
                inputRef={inputRefs[index]}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onPaste={handlePaste}
                inputProps={{
                  maxLength: 1,
                  pattern: '[0-9]*',
                }}
                type="text"
                variant="outlined"
                sx={digitSx}
              />
            ))}
          </Stack>
          {formik.errors.submit && (
            <FormHelperText
              data-testid="errorMSG"
              error
            >
              {formik.errors.submit}
            </FormHelperText>
          )}      
          <Button
            ref={submitButtonRef}
            type="submit"
            fullWidth
            variant="contained"
            disabled={formik.isSubmitting || !formik.isValid || !formik.dirty || formik.values.verificationCode.length < 6}
            size="large"
            sx={{
              my: 3
            }}
            endIcon={<ArrowForwardIcon />}
          >
            Continue
            </Button>
            {showBackLink && (
              <Link
                onClick={() => nextStepCallback(1, email)}
                sx={{
                  cursor: 'pointer'
                }}
              >
                Try a different email
              </Link>
            )}
            {!showBackLink && (
              <>
                <Typography variant="body2" sx={{ color: 'neutral.600' }}>
                  Didn't receive the code? Check your spam folder.
                </Typography>
                <Link
                  onClick={resendCode}
                  variant="body2"
                  sx={{
                    cursor: 'pointer'
                  }}
                >
                  Send me another code.
                </Link>
              </>
            )}            
        </Box>
      </Stack>
    </Box>
  );
}